import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { PermissionService } from '@hta/auth';
import {
    compactNavigation,
    defaultNavigation,
    horizontalNavigation,
    FUTURISTIC_NAVIGATION,
} from 'app/navigation/navigation.data';
import { HtaNavigationItem } from 'app/core/navigation/navigation.types';
import { cloneDeep } from 'lodash-es';

/**
 * Mock API de navegación.
 *
 * Layout `futuristic` (layout activo por defecto):
 *  - Devuelve sólo los MODULE_HEADERS filtrados por módulo habilitado.
 *  - NO incluye children: las sub-opciones se cargan on-demand desde el catálogo
 *    MODULE_ITEMS vía `NavigationService.getModuleChildren(moduleCode)` (filtrado por permiso).
 *
 * Layouts heredados (default / compact / horizontal):
 *  - Mantienen el comportamiento original con children embebidos e hidratados
 *    desde `defaultNavigation`, filtrados por permiso (sólo 2 items tenían mapeo antes).
 */
@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private readonly _compactNavigation: FuseNavigationItem[] =
        compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] =
        defaultNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] =
        horizontalNavigation;

    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _permissionService: PermissionService,
    ) {
        this.registerHandlers();
    }

    registerHandlers(): void {
        // ── Layouts heredados: filtrado por permiso (los 2 items que tenían mapeo) ──
        const legacyPermissionByItem: Record<string, string> = {
            soporteActivar: 'corporativo.soporte.sesion',
            adminPreciosSuscripcion: 'corporativo.suscripcion.precios.gestionar',
        };

        const filterLegacyByPermissions = (items: FuseNavigationItem[]): FuseNavigationItem[] =>
            items
                .filter(item => {
                    const required = item.id ? legacyPermissionByItem[item.id] : undefined;
                    return !required || this._permissionService.hasPermission(required);
                })
                .map(item => item.children?.length
                    ? { ...item, children: filterLegacyByPermissions(item.children) }
                    : item
                );

        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            // Hidratar children de compact/horizontal desde defaultNavigation (comportamiento original)
            this._compactNavigation.forEach((compactNavItem) => {
                this._defaultNavigation.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === compactNavItem.id) {
                        compactNavItem.children = cloneDeep(defaultNavItem.children);
                    }
                });
            });
            this._horizontalNavigation.forEach((horizontalNavItem) => {
                this._defaultNavigation.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === horizontalNavItem.id) {
                        horizontalNavItem.children = cloneDeep(defaultNavItem.children);
                    }
                });
            });

            // ── Layout futuristic: filtrar por `moduleCode` habilitado (enabledModules), children ya presentes ──
            const enabledModuleCodes = new Set(
                this._permissionService.enabledModules().map(m => m.moduleCode)
            );

            const futuristic: HtaNavigationItem[] = cloneDeep(FUTURISTIC_NAVIGATION)
                .filter(item => !item.moduleCode || enabledModuleCodes.has(item.moduleCode))
                .filter(item => {
                    // Filtrar por `requiredPermission` de los hijos (si los tuviera)
                    if (item.children?.length) {
                        item.children = item.children
                            .filter(child => {
                                const required = child.requiredPermission;
                                return !required || this._permissionService.hasPermission(required);
                            });
                        // Si tras filtrar el collapsable no tiene hijos, descartar
                        if (item.type === 'collapsable' && !item.children?.length) {
                            return false;
                        }
                    }
                    return true;
                });

            return [
                200,
                {
                    compact:    filterLegacyByPermissions(cloneDeep(this._compactNavigation)),
                    default:    filterLegacyByPermissions(cloneDeep(this._defaultNavigation)),
                    futuristic,
                    horizontal:  filterLegacyByPermissions(cloneDeep(this._horizontalNavigation)),
                },
            ];
        });
    }
}